/**
 * MIRRORED FILE — twin: src/utils/unitConversions.js
 * Change one, change both. `mirroredUtilParity.test.js` fails the build otherwise.
 *
 * What a catalogue item's packaging states, as the list of conversions it is:
 *
 *     1 CASE   = 12 PCS
 *     1 PCS    = 300 G
 *     1 PALLET = 40 CASE
 *
 * ── Why this exists ───────────────────────────────────────────────────────────
 * `MasterCatalogue` held the same facts as two loose columns: `units_per_case`,
 * a number with no name for the pack it counts, and `unit_size`, a string with
 * both halves of a size welded into it. Two columns is two conversions, so the
 * item form could offer exactly two rows and no more — and the outer pack's NAME
 * had nowhere to be typed at all, which is why that row's left side was a
 * hard-coded "CASE" label that read "1 CASE = 6 CASE" on an item whose own Unit
 * is the case. Both were docs/TECH_DEBT.md #155.
 *
 * `unitConversions` is now the record. Those two columns are two READINGS of it,
 * re-derived on every write, so every existing reader — the cost engine, the
 * assorted-item group factor, `masterCatalogueVendorSync`, the client price list
 * — keeps reading exactly the column it always read and none of them has to
 * learn the array to go on working.
 *
 * ── Which row is which reading ────────────────────────────────────────────────
 * A row's ROLE is decided against the item's own Unit, never by its position, so
 * reordering rows on screen cannot change what gets stored:
 *
 *   size  — "1 <the item's unit> = N <a mass or volume unit>"  -> `unit_size`
 *   pack  — "1 <anything>        = N <the item's unit>"        -> `units_per_case`
 *   other — anything else. Recorded, and read by nothing yet.
 *
 * `size` is tested first, and only accepts a mass/volume unit on its right, so
 * "1 CASE = 6 CASE" on a CASE-counted item still comes out as the pack ratio it
 * has always been rather than as a six-case "size".
 *
 * The first row of each role wins. A second one is kept — throwing away
 * something a person typed is worse than showing it as unused — and the form
 * marks it as stating nothing, rather than the two silently fighting over one
 * column.
 *
 * ── What this does NOT touch ─────────────────────────────────────────────────
 * `uomMeta.level2Name`, the outer pack's name as the rest of the system spells
 * it, is deliberately NOT derived from the pack row here. The client price list
 * reads it as the unit a price is quoted in
 * (`masterCatalogue.controller.js`, the `uom` column), so a per-PCS item that
 * gained a "1 CASE = 12 PCS" row would start printing under "CASE" on a
 * customer-facing document while still being priced per piece. The name lives in
 * the array; teaching that column to ask the array which unit the PRICE is in is
 * a change to a customer-facing document and is its own decision — TECH_DEBT #157.
 */

import { canonicalUnit, splitSizeText } from './unitVocabulary';
import { KG_PER_UNIT, LITRE_PER_UNIT } from './packConversion';

/** One conversion as it is stored: "1 CASE = 12 PCS". */
export type UnitConversion = {
  fromQty: number;
  fromUnit: string;
  toQty: number;
  toUnit: string;
  source: ConversionSource;
};

export type ConversionSource = 'manual' | 'vendor_invoice' | 'derived';

/** Which of the two stored columns a row feeds. */
export type ConversionRole = 'pack' | 'size' | 'other';

/**
 * The units a size row may state on its right.
 *
 * `packConversion`'s two conversion tables rather than a third list of unit
 * names: those keys ARE the units this system can turn into a per-kg or
 * per-litre figure, and they are the same set `itemGroup.parseSize` reads back
 * out of the stored `unit_size` string. A unit neither table knows (PT is a
 * real, recognised unit — see LITRE_PER_UNIT's header for why it is absent; GAL
 * joined at four litres on 2026-09-14) would save a cell that looks filled in
 * and derive nothing behind it: a per-kg price sitting at 0 while the form says
 * the size is set.
 */
export const SIZE_UNITS: readonly string[] = Object.freeze([
  ...Object.keys(KG_PER_UNIT),
  ...Object.keys(LITRE_PER_UNIT),
]);
const SIZE_UNIT_SET = new Set<string>(SIZE_UNITS);

/** Where a row came from. `derived` means this file read it off the legacy columns. */
const VALID_SOURCES = new Set<string>(['manual', 'vendor_invoice', 'derived']);

/** What an outer pack is called when a row has never been told its name. */
export const DEFAULT_PACK_NAME = 'CASE';

/**
 * A validity guard, NOT a number parser — the same distinction, for the same
 * reason, as `packConversion.positive`: writing a parser here would be another
 * `num()` clone, which invariant 8 exists to prevent. Callers coerce first, with
 * the owner (`utils/num.js` on the server, the input's own value on the client),
 * and a row whose quantity is not already a real positive number is refused by
 * the validator before it ever reaches this file.
 */
function positive(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/** True when the unit states a mass or a volume this system can price by. */
export function isSizeUnit(u: unknown): boolean {
  return SIZE_UNIT_SET.has(canonicalUnit(u) || '');
}

/**
 * One conversion in canonical form, or null when it does not state one.
 *
 * The unit names are canonicalised so "1 CS = 12 EA" and "1 CASE = 12 PCS" are
 * one stored fact rather than two — but a name the vocabulary does not know is
 * KEPT as it was typed rather than blanked, the same call
 * `vendorInvoiceInventorySync.buildUom` makes about a level-2 name. A supplier's
 * own word for a pack is still a fact; an empty string is not.
 */
export function normalizeConversion(raw: unknown): UnitConversion | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const fromQty = positive(r.fromQty);
  const toQty = positive(r.toQty);
  const fromRaw = String(r.fromUnit == null ? '' : r.fromUnit).trim();
  const toRaw = String(r.toUnit == null ? '' : r.toUnit).trim();
  if (!fromQty || !toQty || !fromRaw || !toRaw) return null;
  return {
    fromQty,
    fromUnit: canonicalUnit(fromRaw) || fromRaw.toUpperCase(),
    toQty,
    toUnit: canonicalUnit(toRaw) || toRaw.toUpperCase(),
    source: VALID_SOURCES.has(String(r.source)) ? (r.source as ConversionSource) : 'manual',
  };
}

/** The list, with everything that does not state a conversion dropped. */
export function normalizeConversions(raw: unknown): UnitConversion[] {
  if (!Array.isArray(raw)) return [];
  const out: UnitConversion[] = [];
  for (const item of raw) {
    const c = normalizeConversion(item);
    if (c) out.push(c);
  }
  return out;
}

/**
 * The unit `units_per_case` counts — the thing one pack HOLDS.
 *
 * `units_per_case` is defined on the model as "number of inner packs/units in
 * one case" and `unit_size` as "size of one inner unit", so both readings are
 * about the item's CONTENTS. Which unit that is was assumed to be the item's own
 * Unit, and for a hand-entered row it is: somebody types Unit "PCS" and
 * "1 CASE = 12 PCS", and the pieces are both what the item is counted in and
 * what the case holds.
 *
 * A vendor-invoice row is the other way round. Its Unit is the CASE — that is
 * what the invoice billed and what `item_price` is per — and the pieces have no
 * column at all. So "1 CASE = 12 PCS" on such a row named neither side of the
 * old test (`to` is not the item's unit, and `from` is but `to` is not a size),
 * came out `other`, and the item form badged the one row that states the pack as
 * setting nothing. That is 364 of the 369 conversions read off prod's vendor
 * rows.
 *
 * So the anchor is asked for rather than assumed:
 *
 *   the item is the CONTENTS  — some row puts its Unit on the right
 *                               ("1 CASE = 12 PCS", Unit PCS). The counted unit
 *                               is the item's own Unit. Every reading this
 *                               module made before is this case, unchanged.
 *   the item is the PACK      — no row does, and one puts its Unit on the LEFT
 *                               against a count ("1 CASE = 12 PCS", Unit CASE).
 *                               The counted unit is that row's right-hand side.
 *
 * Contents wins when a list somehow states both, because that is the reading
 * every row stored before this existed was written under, and the form marks the
 * row that loses rather than dropping it. A size unit on the right is never a
 * pack (that row states a WEIGHT, not a count of anything), which is what keeps
 * "1 PCS = 3 LBS" from being read as the thing the case holds.
 */
export function packedUnit(conversions: unknown, unit: unknown): string | null {
  const item = canonicalUnit(unit);
  if (!item) return null;
  const list = normalizeConversions(conversions);
  if (list.some((c) => canonicalUnit(c.toUnit) === item)) return item;
  const holder = list.find(
    (c) =>
      canonicalUnit(c.fromUnit) === item && !SIZE_UNIT_SET.has(canonicalUnit(c.toUnit) || '')
  );
  return holder ? canonicalUnit(holder.toUnit) : item;
}

/**
 * Which of the two stored columns this row feeds, against the item's own Unit.
 * See the header — `size` before `pack`, and never by position.
 *
 * `conversions`, the list this row belongs to, is optional and is what lets the
 * anchor be `packedUnit` above rather than the item's Unit. Omitting it answers
 * exactly what this function answered before it took the argument — the caller
 * that has only one row in its hand (`readableSize`, reading a legacy column it
 * just built) is asking about the legacy shape by construction.
 */
export function conversionRole(raw: unknown, unit: unknown, conversions?: unknown): ConversionRole {
  const c = normalizeConversion(raw);
  const item = canonicalUnit(unit);
  if (!c || !item) return 'other';
  const counted = conversions === undefined ? item : packedUnit(conversions, unit) || item;
  const from = canonicalUnit(c.fromUnit);
  const to = canonicalUnit(c.toUnit);
  // Either anchor is accepted for a size so that widening this can only ever
  // turn an `other` into a reading, never take one away: `from === item` is the
  // old test, kept whole, and `from === counted` is the piece a pack row just
  // named. They are the same test on every row written before this.
  if (to && SIZE_UNIT_SET.has(to) && (from === counted || from === item)) return 'size';
  // `to === counted`, not `to === item`: the two are the same unit unless a row
  // put the item's Unit on the left, and in that case no row has the item's Unit
  // on the right at all — `packedUnit` would have answered the item's Unit if
  // one did.
  if (to === counted) return 'pack';
  return 'other';
}

/** How many of the right-hand unit are in ONE of the left-hand unit. */
export function conversionRatio(c: UnitConversion): number {
  return c.toQty / c.fromQty;
}

/**
 * A ratio as the text `unit_size` stores.
 *
 * Six decimals, matching `itemGroup.weightTierFromSize`'s own precision: fine
 * enough for any pack this system holds and coarse enough to erase the floating
 * point dust a division leaves behind, so "1 PCS = 300 G" cannot come back as
 * 299.99999999999994.
 */
export function formatQty(n: number): string {
  return String(Math.round(n * 1e6) / 1e6);
}

/**
 * True when a row says the item's own Unit equals itself — "1 PCS = 1 LBS" on a
 * row already counted in LBS, or the bare "1 LBS = 1 LBS".
 *
 * ── What it is for ───────────────────────────────────────────────────────────
 * It is one unit written twice. Nothing can be converted with it, `buildGraph`
 * links the pair at a ratio of 1 so no price moves through it, and the item form
 * prints it at somebody who then has to work out that it means nothing. The
 * owner reported it as noise on the Master Catalogue screen (2026-09-10) and it
 * is: 26 prod rows and 11 QA rows, every one of them manufactured rather than
 * typed.
 *
 * ── Where they came from ─────────────────────────────────────────────────────
 * `masterCatalogueVendorSync.conversionsFromPackFacts` names the inner unit
 * `PCS` when an invoice line never named one, and its SIZE branch accepted any
 * positive amount — so a reviewed Unit Size cell reading "1 LB" wrote
 * "1 PCS = 1 LBS". Its own PACK branch, five lines above, has refused a ratio of
 * exactly 1 since it was written ("A ratio of exactly 1 states NOTHING and must
 * not be written"); the size branch simply never got the same rule. That is
 * fixed at the writer as well, so this predicate is about the rows already
 * stored rather than a permanent crutch.
 *
 * ── Why the test is the RIGHT-hand unit, and not "the ratio is 1" ────────────
 * A ratio of 1 between two units the item actually uses is a FACT, and one this
 * system prices off. `Quality Coconut Water` is counted in PCS and states
 * "1 PCS = 1 L" — a one-litre bottle. Its ratio is 1, its `uom` column prints
 * "1 L", and hiding it would blank a customer-facing column and cost the row its
 * per-litre price. The row that states nothing is specifically the one whose
 * RIGHT side is the unit the item is already counted in, because that is the
 * side `conversionRole` anchors both readings against.
 *
 * A self-loop is only caught when its ratio is 1. "1 CASE = 6 CASE" on a
 * CASE-counted item is a real pack ratio — the legacy shape `units_per_case` has
 * always read, and `describePackaging`'s header says so — and it stays.
 */
export function restatesOwnUnit(raw: unknown, unit: unknown): boolean {
  const c = normalizeConversion(raw);
  const item = canonicalUnit(unit);
  if (!c || !item) return false;
  if (Math.round(conversionRatio(c) * 1e6) / 1e6 !== 1) return false;
  return canonicalUnit(c.toUnit) === item;
}

/**
 * The conversions worth SHOWING — the list minus the rows that only restate the
 * item's own Unit.
 *
 * A filter on what a row's conversions SAY, never on what they ARE. It is
 * deliberately NOT applied inside `conversionsForItem` or `legacyPackFields`:
 * those two own what a row's conversions ARE and what the two stored columns read
 * off them, and quietly dropping rows there would change stored data on the next
 * save of an unrelated field. Its callers are screens, documents, and (server
 * twin) the price graph `costEngine.computeGraphPriceLevels`, 2026-10-01: a
 * restating row says nothing, so it is no edge to price across.
 */
export function shownConversions(conversions: unknown, unit: unknown): UnitConversion[] {
  return normalizeConversions(conversions).filter((c) => !restatesOwnUnit(c, unit));
}

/** "1 CASE = 12 PCS", for the screen and for warning strings. */
export function describeConversion(raw: unknown): string {
  const c = normalizeConversion(raw);
  if (!c) return '';
  return `${formatQty(c.fromQty)} ${c.fromUnit} = ${formatQty(c.toQty)} ${c.toUnit}`;
}

/**
 * The two legacy columns this list states. Null for a reading the list does not
 * make, because missing is not zero and a pack ratio nobody stated must not
 * default to one.
 */
export function legacyPackFields(
  conversions: unknown,
  unit: unknown
): { units_per_case: number | null; unit_size: string | null } {
  let unitsPerCase: number | null = null;
  let unitSize: string | null = null;
  const list = normalizeConversions(conversions);
  for (const c of list) {
    const role = conversionRole(c, unit, list);
    if (role === 'pack' && unitsPerCase === null) {
      unitsPerCase = Math.round(conversionRatio(c) * 1e6) / 1e6;
    } else if (role === 'size' && unitSize === null) {
      unitSize = `${formatQty(conversionRatio(c))} ${canonicalUnit(c.toUnit)}`;
    }
  }
  return { units_per_case: unitsPerCase, unit_size: unitSize };
}

/**
 * The rows an item that predates this array already implies — how every one of
 * the ~1,500 existing catalogue rows gets its conversions without a migration.
 *
 * `packName` is `uomMeta.level2Name` where the vendor sync recorded one, and
 * `CASE` where it did not, which is exactly what the form used to hard-code.
 * A weight or volume names no pack, so it counts as no name (D169.7): the sync
 * stored "LBS" on a pound-counted row, "1 LBS = 20 LBS" then read as a SIZE,
 * disagreed with a saved "1 CASE = 20 LBS" and replaced it on every read, so
 * Edit Item reopened on "1 LBS = 20 LBS" and convert-all found no case (VPO-00213).
 */
export function conversionsFromLegacyFields(item: {
  unit?: unknown;
  units_per_case?: unknown;
  unit_size?: unknown;
  packName?: unknown;
}): UnitConversion[] {
  const unit = String(item && item.unit != null ? item.unit : '').trim();
  const out: UnitConversion[] = [];
  if (!unit) return out;

  const perCase = positive(typeof item.units_per_case === 'number' ? item.units_per_case : null);
  if (perCase) {
    const packRaw = String(item.packName == null ? '' : item.packName).trim();
    const packName = canonicalUnit(packRaw) || packRaw.toUpperCase();
    out.push({
      fromQty: 1,
      fromUnit: packName && !SIZE_UNIT_SET.has(packName) ? packName : DEFAULT_PACK_NAME,
      toQty: perCase,
      toUnit: unit,
      source: 'derived',
    });
  }

  // `splitSizeText` returns the two halves ONLY for a string that is exactly a
  // number and a unit. A pack config ("12x28oz"), a size grade ("16-20") and a
  // bare unit all come back null on purpose — see `readableSize` below for what
  // that refusal protects.
  const size = splitSizeText(item.unit_size);
  const sizeUnit = size ? canonicalUnit(size.unit) : null;
  if (size && sizeUnit && SIZE_UNIT_SET.has(sizeUnit)) {
    const amount = positive(Number(size.amount));
    if (amount) {
      out.push({ fromQty: 1, fromUnit: unit, toQty: amount, toUnit: sizeUnit, source: 'derived' });
    }
  }
  return out;
}

/**
 * True when a stored `unit_size` is one this form can show as a row — and so
 * one a person can be held to have removed by removing that row.
 */
export function readableSize(unitSize: unknown, unit: unknown): boolean {
  return conversionsFromLegacyFields({ unit, unit_size: unitSize }).some(
    (c) => conversionRole(c, unit) === 'size'
  );
}

/**
 * `legacyPackFields`, but against what the row ALREADY stores.
 *
 * A size the list does not state is cleared only when the stored one was itself
 * readable — when the form could have shown it as a row and the person could
 * have chosen to remove it. A stored size no row was ever opened for ("12x28oz")
 * is left exactly as found: removing something nobody was shown is not an edit
 * anyone made, and it is a real fact about the pack that this form has no way to
 * re-type.
 *
 * `units_per_case` needs no such rule. It is a number, so there is no shape of
 * it the form cannot show, and clearing it is always something someone did.
 */
export function legacyPackFieldsFor(
  conversions: unknown,
  stored: { unit?: unknown; unit_size?: unknown }
): { units_per_case: number | null; unit_size: string | null } {
  const unit = stored && stored.unit;
  const derived = legacyPackFields(conversions, unit);
  const storedSize = stored && stored.unit_size ? String(stored.unit_size).trim() : '';
  if (derived.unit_size === null && storedSize && !readableSize(storedSize, unit)) {
    return { units_per_case: derived.units_per_case, unit_size: storedSize };
  }
  return derived;
}

/**
 * The packaging as a customer-facing phrase: "CARTON of 12 x 794 G".
 *
 * The client price list prints this in its UOM column, beside a "Unit" column
 * that names the unit the PRICE is per. Before this, that column printed
 * `uomMeta.level2Name` — the outer pack's name — next to a price per piece, so a
 * per-BTL item read "UOM: CARTON" on a document a customer orders from
 * (TECH_DEBT #157). The two columns now answer two different questions and both
 * answers are true: what you get, and what the money is per.
 *
 * Describes the SAME two rows `legacyPackFields` reads, first-of-each-role, so
 * the sentence on the document and the numbers in the columns cannot describe
 * different rows.
 *
 * A pack row whose two units are the same canonical unit ("1 CASE = 6 CASE" on a
 * CASE-counted item) contributes NOTHING here. It is a real stored pack ratio and
 * `units_per_case` still reads it, but it does not NAME a pack — printing "CASE
 * of 6" beside a Unit column that also says CASE puts a contradiction on a
 * customer's document, which is the defect this function exists to end rather
 * than to relocate.
 *
 * Empty string when the item states no packaging at all, and empty is the right
 * answer: the previous column was filled by falling back to the item's own unit,
 * which is what made it duplicate the Unit column and never look wrong.
 */
export function describePackaging(conversions: unknown, unit: unknown): string {
  let pack = '';
  let size = '';
  let sawPack = false;
  let sawSize = false;
  // `shownConversions`, so a row that only restates the item's own Unit cannot
  // fill this column. "1 LBS = 1 LBS" printed a `uom` of "1 LBS" beside a
  // `packaging` column already reading LBS — the same fact twice, which is
  // precisely the duplication the header above says this function exists to end.
  // Four prod rows print that today and print nothing after this.
  const list = shownConversions(conversions, unit);
  for (const c of list) {
    const role = conversionRole(c, unit, list);
    if (role === 'pack' && !sawPack) {
      sawPack = true;
      const from = canonicalUnit(c.fromUnit) || c.fromUnit;
      const to = canonicalUnit(c.toUnit) || c.toUnit;
      // `!SIZE_UNIT_SET.has(to)` is the same refusal as `from !== to` beside it,
      // in the other shape it comes in. An item counted in KILOGRAMS makes
      // `conversionRole` read "1 PCS = 40 KG" — the WEIGHT of one piece — as the
      // pack row, because its right-hand unit is the item's own unit. That is
      // the correct reading for `units_per_case`; it is not a pack NAME, and
      // printing "PCS of 40" beside a Unit column that says KG puts the same
      // contradiction on a customer's document that "CASE of 6" beside CASE did.
      // Nine prod rows have exactly this shape ("Beef - Oxtail", 1 PCS = 1 KG,
      // would have printed "PCS of 1"), and they print an empty column today.
      if (from !== to && !SIZE_UNIT_SET.has(to)) pack = `${from} of ${formatQty(conversionRatio(c))}`;
    } else if (role === 'size' && !sawSize) {
      sawSize = true;
      size = `${formatQty(conversionRatio(c))} ${canonicalUnit(c.toUnit)}`;
    }
  }
  if (pack && size) return `${pack} x ${size}`;
  return pack || size;
}

/**
 * The conversions to SHOW for a stored item — the array when it is still the
 * truth about the row, and the legacy columns when they are.
 *
 * `masterCatalogueVendorSync` writes `units_per_case` / `unit_size` straight
 * onto the document, without going through the item controller and so without
 * touching this array. A row synced from an invoice after somebody edited it by
 * hand would otherwise open showing the older array while the catalogue prices
 * off the newer columns — the denormalised-copy drift this codebase keeps
 * paying for, in a new place.
 *
 * So the array only wins while it still READS as what the columns say. The
 * moment they part company the columns are the newer fact and the array is
 * rebuilt from them, losing only what the columns cannot hold (a custom outer
 * pack name, a third conversion) — which is exactly what the other writer just
 * overwrote anyway.
 */
export function conversionsForItem(item: {
  unit?: unknown;
  units_per_case?: unknown;
  unit_size?: unknown;
  unitConversions?: unknown;
  uomMeta?: { level2Name?: unknown } | null;
} | null | undefined): UnitConversion[] {
  const row = item || ({} as Record<string, any>);
  const unit = row.unit;
  const fromLegacy = conversionsFromLegacyFields({
    unit,
    units_per_case: row.units_per_case,
    unit_size: row.unit_size,
    packName: row.uomMeta && row.uomMeta.level2Name,
  });

  const list = normalizeConversions(row.unitConversions);
  if (!list.length) return fromLegacy;

  // Nothing to be newer THAN. The drift check below exists because the two
  // columns can be rewritten underneath the array by a writer that does not go
  // through the item controller, so a disagreement means the columns are the
  // newer fact — but columns that state nothing are not a newer fact, they are
  // no fact, and falling through to them throws the list away and shows an item
  // with no packaging at all.
  //
  // That is the shape every vendor-invoice row has: `masterCatalogueVendorSync`
  // writes `unitConversions` and leaves `units_per_case` / `unit_size` null
  // (they are the HAND-ENTRY columns, and filling them from an invoice would
  // move the assorted-item group factor and the pricing candidate's ranked pack
  // count on live rows). A "1 PCS = 400 G" written that way reads as the SIZE
  // column against a PCS-counted item, disagreed with the null column, and
  // vanished on 36 of the 252 prod rows this affects.
  if (!fromLegacy.length) return list;

  // Both readings taken the same way, so "300g" in the column and "300 G" off
  // the array are not mistaken for a disagreement.
  const stored = { unit, unit_size: row.unit_size };
  const fromArray = legacyPackFieldsFor(list, stored);
  const fromColumns = legacyPackFieldsFor(fromLegacy, stored);
  return fromArray.units_per_case === fromColumns.units_per_case &&
    fromArray.unit_size === fromColumns.unit_size
    ? list
    : fromLegacy;
}
